import {callLLM} from '../../../../../../utils/llmApi';

// Default experts (Dana Scully and Fox Mulder) to return when no description or ambiguous
const DEFAULT_EXPERTS = [
    {
        handle: 'dana.scully',
        displayName: 'Dana Scully',
        title: 'Security Analyst',
        office: 'Nuclear Proliferation & Strategic Threats',
        organization: 'FBI',
        email: 'dana.scully@x-files.com',
        cellPhone: '202-555-0701',
        photo: '/assets/dana.jpg',
        expertise: 'Security analyst focused on nuclear proliferation, missile programs, post-strike assessments, and anti-Western strategic alliances.',
        specialties: ['Nuclear Proliferation', 'Missile Programs', 'Military Assessments', 'Strategic Alliances', 'Nonproliferation Policy'],
        relevanceReason: 'Deep expertise in nuclear proliferation and strategic threat assessment.',
        matchScore: 95
    },
    {
        handle: 'fox.mulder',
        displayName: 'Fox Mulder',
        title: 'Strategic Supply-Chain Analyst',
        office: 'National Security & Critical Infrastructure',
        organization: 'FBI',
        email: 'fox.mulder@x-files.com',
        cellPhone: '202-555-0801',
        photo: '/assets/fox.jpg',
        expertise: 'Strategic supply-chain and national security analyst focused on foreign ownership, critical-minerals dependencies, and China\'s influence on U.S. energy industries.',
        specialties: ['Critical Minerals', 'China Supply Chains', 'Foreign Investment', 'Energy Security', 'Policy Risk'],
        relevanceReason: 'Specialized knowledge in critical mineral supply chains and China\'s strategic influence.',
        matchScore: 92
    }
];

/**
 * Generate fake expert profiles based on description using LLM
 * @param {string} description - The instructions/description for the type of experts needed
 * @returns {Promise<Array>} Array of expert objects
 */
export async function generateExpertsWithLLM(description) {
    // Add 2 second delay to emulate loading
    await new Promise(resolve => setTimeout(resolve, 2000));

    // If no description provided, return default experts
    if (!description || description.trim().length === 0) {
        return DEFAULT_EXPERTS;
    }


    const systemPrompt = `You are an expert profile generator that creates realistic fake expert profiles based on requirements.

You will receive a description of the type of expert needed, and you should generate 2-3 relevant expert profiles.

CRITICAL RULES:
1. Return ONLY a valid JSON array of expert objects
2. Each expert object must have these exact fields:
   - handle: string (username like 'firstname.lastname')
   - displayName: string (full name)
   - title: string (job title)
   - office: string (department/office name)
   - organization: string (organization name)
   - email: string (professional email)
   - cellPhone: string (format: XXX-XXX-XXXX)
   - expertise: string (detailed paragraph about their expertise)
   - specialties: array of 4-6 strings (specific areas of expertise)
   - relevanceReason: string (why they match the requirements)
   - matchScore: number (70-98, how well they match)
3. DO NOT include a "uid" field - not needed for generated experts
4. DO NOT include a "photo" field - avatars will be generated from initials (only default experts have photos)
5. Make experts diverse and realistic
6. If the description is too vague, ambiguous, or doesn't provide enough detail to create meaningful experts, return null
7. Return ONLY the JSON array - no markdown code blocks, no explanations

EXAMPLES OF GOOD EXPERT OBJECTS (Dana Scully and Fox Mulder):
${JSON.stringify(DEFAULT_EXPERTS, null, 2)}

If description is unclear (e.g., "some expert", "idk", "help", single word), return: null`;

    const userPrompt = `Generate 2-3 expert profiles based on this description:

${description}`;

    const messages = [
        {role: 'system', content: systemPrompt},
        {role: 'user', content: userPrompt}
    ];

    // Must be registered in dash-api/services/litellm-service/config.yaml when OPENAI_ROUTING_MODE=litellm_sidecar.
    const model = process.env.REACT_APP_LLM_MODEL || 'gpt-4.1-mini';

    try {
        const response = await callLLM(messages, model);

        // Extract content from response
        const content = response.choices?.[0]?.message?.content || response.content;

        if (!content) {
            console.warn('No content in LLM response, returning default experts');
            return DEFAULT_EXPERTS;
        }

        // Clean up the response
        let jsonStr = content.trim();

        // Remove markdown code blocks if present
        if (jsonStr.startsWith('```')) {
            jsonStr = jsonStr.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
        }

        // Check if LLM returned null (ambiguous description)
        if (jsonStr === 'null' || jsonStr === 'NULL') {
            console.log('LLM returned null - description too ambiguous, using default experts');
            return DEFAULT_EXPERTS;
        }

        // Parse the JSON
        const experts = JSON.parse(jsonStr);

        // Validate it's an array with at least one expert
        if (!Array.isArray(experts) || experts.length === 0) {
            console.warn('Invalid response format, returning default experts');
            return DEFAULT_EXPERTS;
        }

        // Validate each expert has required fields (uid not required for generated experts)
        const validExperts = experts.filter(expert =>
            expert.displayName &&
            expert.title &&
            expert.expertise &&
            Array.isArray(expert.specialties)
        );

        if (validExperts.length === 0) {
            console.warn('No valid experts in response, returning default experts');
            return DEFAULT_EXPERTS;
        }

        // Remove photo and uid fields if present (we don't want headshots, and uid is not needed)
        return validExperts.map(expert => {
            const {photo, uid, ...expertCleaned} = expert;
            return expertCleaned;
        });

    } catch (error) {
        console.error('Error generating experts with LLM:', error);
        // On error, return default experts
        return DEFAULT_EXPERTS;
    }
}
